// ── Runtime capture of brand-default image URLs ──────────────────────────
//
// Reports `{slot_id, url}` pairs to the backend whenever an editable
// image renders using its hard-coded `fallback` prop (i.e. no DB
// override active). The backend upserts these with $setOnInsert
// semantics — first snapshot wins — so `/admin/images` can display
// the true brand default as a thumbnail for slots whose registry
// entry left `fallback: null`.
//
// Design notes:
//   • Dedupe per session via an in-memory Set (`slot::url`) so we
//     never POST the same pair twice per tab.
//   • Batched flush at ~5s cadence with a max-batch cap so a page
//     with hundreds of images sends one round-trip, not hundreds.
//   • sendBeacon() on `pagehide`/`beforeunload` so no observations
//     are lost if the tab closes before the timer fires.
//   • All errors swallowed — this is telemetry, never critical path.

const API = process.env.REACT_APP_BACKEND_URL || "";
const FLUSH_DELAY_MS = 5000;
const MAX_BATCH = 250;

const seen  = new Set();
const queue = [];
let flushTimer = null;

const scheduleFlush = () => {
  if (flushTimer || queue.length === 0) return;
  flushTimer = window.setTimeout(flushNow, FLUSH_DELAY_MS);
};

const flushNow = async () => {
  window.clearTimeout(flushTimer);
  flushTimer = null;
  if (queue.length === 0) return;
  // Drain the queue up to MAX_BATCH — anything beyond that stays for
  // the next round.
  const batch = queue.splice(0, MAX_BATCH);
  const payload = JSON.stringify({ entries: batch });
  try {
    await fetch(`${API}/api/slot-defaults/bulk`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
  } catch (_e) {
    // Swallow — silent telemetry.
  }
  // If more accumulated during the flight, schedule the next flush.
  if (queue.length > 0) scheduleFlush();
};

// Fire-and-forget send on tab close. sendBeacon is the browser API
// designed for exactly this: a small unrecoverable POST that runs
// even after the tab is gone.
const flushViaBeacon = () => {
  if (queue.length === 0) return;
  if (typeof navigator === "undefined" || !navigator.sendBeacon) return;
  const batch = queue.splice(0, MAX_BATCH);
  const blob = new Blob([JSON.stringify({ entries: batch })], { type: "application/json" });
  try {
    navigator.sendBeacon(`${API}/api/slot-defaults/bulk`, blob);
  } catch (_e) { /* ignore */ }
};

if (typeof window !== "undefined") {
  // `pagehide` covers back-forward cache transitions on Safari, which
  // don't reliably fire `beforeunload`. Belt-and-braces both.
  window.addEventListener("pagehide", flushViaBeacon);
  window.addEventListener("beforeunload", flushViaBeacon);
}

// Public API — call from any component that knows its slot's default URL.
// `url` should be the resolved fallback (not the DB override).
export const reportSlotDefault = (slot, url, page = null) => {
  if (!slot || !url) return;
  // Skip our own storage URLs — those are DB overrides, not defaults.
  // If they somehow leaked through, we don't want to persist them as
  // the "brand default" and cross-wire the reset flow.
  if (typeof url === "string" && url.includes("/api/files/xaluca-cms/slots/")) return;
  const key = `${slot}::${url}`;
  if (seen.has(key)) return;
  seen.add(key);
  queue.push({
    slot_id: slot,
    url,
    page: page || (typeof window !== "undefined" ? window.location.pathname : null),
  });
  scheduleFlush();
};

export default reportSlotDefault;
